<template>
  <div class="container-fluid mt-4 coros-view">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0"><i class="bi bi-smartwatch me-2"></i>Coros</h2>
        <small class="text-muted">
          Entrenamiento, sueño y métricas · MCP oficial de Coros
          (<a href="https://mcp.coros.com/mcp" target="_blank" rel="noopener">mcp.coros.com</a>)
        </small>
        <div v-if="status?.configured && status.lastSyncAt" class="small text-muted mt-1">
          Última sincronización: {{ status.lastSyncAt }}
          <span v-if="status.lastMcpSyncAt"> · MCP: {{ status.lastMcpSyncAt }}</span>
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap" v-if="status?.configured">
        <button type="button" class="btn btn-outline-primary" :disabled="sincronizando" @click="sincronizar(28)">
          <span v-if="sincronizando && syncDays === 28" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-repeat me-1"></i>
          Sync 28 días
        </button>
        <button type="button" class="btn btn-outline-primary" :disabled="sincronizando" @click="sincronizar(90)">
          <span v-if="sincronizando && syncDays === 90" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-cloud-download me-1"></i>
          Sync 90 días
        </button>
        <router-link to="/perfil" class="btn btn-outline-secondary">
          <i class="bi bi-gear me-1"></i>Credenciales
        </router-link>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="mensaje" class="alert alert-success">{{ mensaje }}</div>

    <div v-if="!status?.configured" class="card border-0 bg-light">
      <div class="card-body">
        <h5 class="card-title">Configura Coros</h5>
        <p class="mb-2 small">
          Usa el email y contraseña de tu cuenta Coros Training Hub en
          <router-link to="/perfil">Editar perfil</router-link> (sección Coros, solo administradores).
        </p>
        <p class="mb-0 small text-muted">
          El sueño se lee del MCP oficial (<code>querySleepOverview</code>) al sincronizar.
        </p>
      </div>
    </div>

    <template v-else>
      <CorosEstadoEntrenamiento
        v-if="dashboard?.trainingStatus"
        class="mb-4"
        :status="dashboard.trainingStatus"
        :hoy-entreno="dashboard.hoyEntreno || (esHoy(proximoEntreno) ? proximoEntreno : null)"
      />

      <!-- Próximo entreno destacado -->
      <div v-if="proximoEntreno && !esHoy(proximoEntreno)" class="card border-0 shadow-sm mb-4 coros-next-workout overflow-hidden">
        <div class="card-body py-3">
          <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <div class="coros-next-icon rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                <i class="bi bi-lightning-charge-fill fs-4"></i>
              </div>
              <div class="min-w-0">
                <div class="small text-uppercase fw-semibold coros-next-label mb-1">Próximo entreno</div>
                <div class="fs-5 fw-bold text-truncate">{{ proximoEntreno.name || 'Entreno planificado' }}</div>
                <div class="small mt-1 d-flex flex-wrap align-items-center gap-2">
                  <span class="badge bg-white bg-opacity-25">{{ fmtEntrenoDia(proximoEntreno) }}</span>
                  <span v-if="proximoEntreno.sport_name" class="badge bg-white bg-opacity-25">{{ proximoEntreno.sport_name }}</span>
                  <span v-if="proximoEntreno.estimated_time_seconds">{{ fmtDuration(proximoEntreno.estimated_time_seconds) }}</span>
                  <span v-if="proximoEntreno.exercise_count" class="text-white-50">{{ proximoEntreno.exercise_count }} pasos</span>
                </div>
                <div
                  v-if="entrenoDescripcion(proximoEntreno)"
                  class="small mt-2 coros-desc-preview text-white-50"
                >
                  {{ entrenoDescripcionPreview(proximoEntreno, 3) }}
                </div>
              </div>
            </div>
            <div class="d-flex gap-2 flex-shrink-0">
              <button type="button" class="btn btn-light btn-sm" @click="verEntreno(proximoEntreno)">
                <i class="bi bi-list-ul me-1"></i>Detalle
              </button>
              <button type="button" class="btn btn-outline-light btn-sm" @click="tabActivo = 'entrenos'">
                Calendario
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="dashboard?.latest" class="row g-3 mb-4">
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold text-primary">{{ fmtNum(dashboard.latest.hrv) }}</div>
            <div class="small text-muted text-uppercase">HRV (ms)</div>
          </div>
        </div>
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold">{{ fmtNum(dashboard.latest.rhr) }}</div>
            <div class="small text-muted text-uppercase">FC reposo</div>
          </div>
        </div>
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold">{{ fmtNum(dashboard.latest.training_load) }}</div>
            <div class="small text-muted text-uppercase">Carga (ATI)</div>
          </div>
        </div>
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold">{{ fmtNum(dashboard.latest.vo2max) }}</div>
            <div class="small text-muted text-uppercase">VO2max</div>
          </div>
        </div>
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold">{{ fmtSleep(dashboard.latest.sleep_minutes) }}</div>
            <div class="small text-muted text-uppercase">Sueño</div>
            <div v-if="dashboard.latest.sleep_score != null" class="small text-muted">
              Score {{ dashboard.latest.sleep_score }}
            </div>
          </div>
        </div>
        <div class="col-6 col-md-4 col-lg-2">
          <div class="card h-100 border-0 shadow-sm text-center py-3">
            <div class="fs-4 fw-bold text-success">{{ fmtDec(dashboard.latest.stamina_level) }}</div>
            <div class="small text-muted text-uppercase">Carrera</div>
          </div>
        </div>
      </div>

      <ul class="nav nav-tabs mb-3">
        <li class="nav-item" v-for="t in tabs" :key="t.id">
          <button type="button" class="nav-link" :class="{ active: tabActivo === t.id }" @click="tabActivo = t.id">
            {{ t.label }}
          </button>
        </li>
      </ul>

      <div v-if="cargando" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>

      <template v-else>
        <!-- Métricas -->
        <div v-show="tabActivo === 'metricas'" class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-white">
            <h5 class="mb-0">Métricas diarias</h5>
          </div>
          <div class="card-body">
            <div v-if="chartHrv.labels?.length" class="coros-chart-wrap mb-4">
              <Line :data="chartHrv" :options="chartOpts" />
            </div>
            <div v-if="chartFitness.labels?.length" class="coros-chart-wrap mb-2">
              <Line :data="chartFitness" :options="chartOptsFitness" />
            </div>
            <div v-if="!chartHrv.labels?.length && !chartFitness.labels?.length" class="text-muted text-center py-4">
              Sin datos de métricas. Sincroniza primero.
            </div>
          </div>
        </div>

        <!-- Sueño -->
        <div v-show="tabActivo === 'sueno'" class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-white">
            <h5 class="mb-0">Sueño</h5>
          </div>
          <div class="card-body">
            <div v-if="chartSleep.labels?.length" class="coros-chart-wrap mb-4">
              <Line :data="chartSleepData" :options="chartSleep.stacked ? chartOptsStacked : chartOpts" />
            </div>
            <div v-if="sleepNoches.length" class="table-responsive">
              <table class="table table-sm table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Total</th>
                    <th>Profundo</th>
                    <th>REM</th>
                    <th>Ligero</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="n in sleepNochesSlice" :key="n.dia">
                    <td>{{ fmtDiaCorto(n.dia) }}</td>
                    <td>{{ fmtSleep(n.total) }}</td>
                    <td>{{ fmtSleepShort(n.deep) }}</td>
                    <td>{{ fmtSleepShort(n.rem) }}</td>
                    <td>{{ fmtSleepShort(n.light) }}</td>
                    <td>{{ n.score != null ? n.score : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!chartSleep.labels?.length && !sleepNoches.length" class="text-muted text-center py-4">
              <p class="mb-2">Sin datos de sueño sincronizados.</p>
              <p class="small mb-0">
                El sueño sale del MCP oficial de Coros. Pulsa «Sync 28 días» para traer el historial.
              </p>
            </div>
          </div>
        </div>

        <!-- Actividades -->
        <div v-show="tabActivo === 'actividades'" class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Actividades</h5>
            <span class="badge bg-secondary">{{ actividades.length }}</span>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-sm table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Deporte</th>
                    <th>Nombre</th>
                    <th>Duración</th>
                    <th>Distancia</th>
                    <th>FC media</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in actividades" :key="a.activity_id">
                    <td class="text-nowrap">{{ fmtActivityDate(a) }}</td>
                    <td>{{ a.sport_name || '—' }}</td>
                    <td>{{ a.name || '—' }}</td>
                    <td>{{ fmtDuration(a.duration_seconds) }}</td>
                    <td>{{ fmtDistance(a.distance_meters) }}</td>
                    <td>{{ a.avg_hr ?? '—' }}</td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-secondary" @click="verDetalle(a)">
                        <i class="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!actividades.length" class="text-muted text-center py-4">No hay actividades sincronizadas.</div>
          </div>
        </div>

        <!-- Entrenos y calendario -->
        <div v-show="tabActivo === 'entrenos'" class="row g-3">
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Biblioteca de entrenos</h5>
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="cargandoRemoto" @click="cargarWorkouts(true)">
                  Actualizar
                </button>
              </div>
              <ul class="list-group list-group-flush">
                <li v-for="w in workouts" :key="w.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-start gap-2">
                    <div class="min-w-0">
                      <div class="fw-semibold">{{ w.name }}</div>
                      <div class="small text-muted mt-1">
                        <span v-if="w.sport_name" class="badge bg-light text-dark border me-1">{{ w.sport_name }}</span>
                        <span>{{ fmtDuration(w.estimated_time_seconds) }}</span>
                        <span v-if="w.exercise_count"> · {{ w.exercise_count }} pasos</span>
                      </div>
                      <div
                        v-if="entrenoDescripcion(w)"
                        class="small text-muted mt-2 coros-desc-preview"
                      >
                        {{ entrenoDescripcionPreview(w, 4) }}
                      </div>
                      <div v-else-if="w.steps?.length" class="small text-muted mt-2 coros-steps-preview">
                        <span v-for="(s, i) in w.steps.slice(0, 4)" :key="i" class="d-block text-truncate">
                          {{ entrenoPasoLabel(s) }}<span v-if="s.duration_seconds"> · {{ fmtDuration(s.duration_seconds) }}</span><span v-if="fmtStepIntensity(s)"> · {{ fmtStepIntensity(s) }}</span>
                        </span>
                        <span v-if="w.steps.length > 4" class="text-muted">+ {{ w.steps.length - 4 }} más…</span>
                      </div>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-secondary flex-shrink-0" @click="verEntreno(w)">
                      <i class="bi bi-eye"></i>
                    </button>
                  </div>
                </li>
              </ul>
              <div v-if="!workouts.length" class="card-body text-muted small">Pulsa Actualizar para cargar desde Coros.</div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Próximos entrenos</h5>
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="cargandoRemoto" @click="cargarSchedule(true)">
                  Actualizar
                </button>
              </div>
              <div class="card-body p-0">
                <div v-if="scheduleItems.length">
                  <div v-for="(item, i) in scheduleItems" :key="i" class="border-bottom px-3 py-3">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                      <div class="min-w-0">
                        <div class="small text-muted mb-1">{{ item.day_label || item.happen_day }}</div>
                        <div class="fw-semibold">{{ item.name || 'Entreno sin nombre' }}</div>
                        <div class="small mt-1">
                          <span v-if="item.sport_name" class="badge bg-primary-subtle text-primary border me-1">{{ item.sport_name }}</span>
                          <span v-if="item.estimated_time_seconds" class="text-muted">{{ fmtDuration(item.estimated_time_seconds) }}</span>
                          <span v-if="item.exercise_count" class="text-muted"> · {{ item.exercise_count }} pasos</span>
                        </div>
                        <div
                          v-if="entrenoDescripcion(item)"
                          class="small text-muted mt-2 coros-desc-preview"
                        >
                          {{ entrenoDescripcionPreview(item, 3) }}
                        </div>
                        <div v-else-if="item.steps?.length" class="small text-muted mt-2">
                          <span v-for="(s, j) in item.steps.slice(0, 3)" :key="j" class="d-block text-truncate">
                            {{ entrenoPasoLabel(s) }}<span v-if="s.duration_seconds"> · {{ fmtDuration(s.duration_seconds) }}</span><span v-if="fmtStepIntensity(s)"> · {{ fmtStepIntensity(s) }}</span>
                          </span>
                        </div>
                      </div>
                      <button type="button" class="btn btn-sm btn-outline-secondary flex-shrink-0" @click="verEntreno(item)">
                        <i class="bi bi-list-ul"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="card-body text-muted small">No hay entrenos planificados a partir de hoy.</div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Modal detalle actividad -->
    <div v-if="detalle" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,.4)">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ modalTitulo }}</h5>
            <button type="button" class="btn-close" @click="cerrarDetalle"></button>
          </div>
          <div class="modal-body">
            <div v-if="cargandoDetalle" class="text-center py-4">
              <div class="spinner-border text-primary"></div>
            </div>
            <template v-else-if="entrenoSel">
              <div class="mb-3 small text-muted">
                <span v-if="entrenoSel.sport_name" class="badge bg-light text-dark border me-1">{{ entrenoSel.sport_name }}</span>
                <span v-if="entrenoSel.estimated_time_seconds">{{ fmtDuration(entrenoSel.estimated_time_seconds) }}</span>
                <span v-if="entrenoSel.estimated_distance_meters"> · {{ fmtDistance(entrenoSel.estimated_distance_meters) }}</span>
                <span v-if="entrenoSel.training_load"> · {{ entrenoSel.training_load }} TL</span>
                <span v-if="entrenoSel.day_label"> · {{ entrenoSel.day_label }}</span>
              </div>
              <div v-if="entrenoDescripcion(entrenoSel)" class="mb-3">
                <div class="small fw-semibold text-muted mb-1">Descripción</div>
                <div class="coros-desc-full small">{{ entrenoDescripcion(entrenoSel) }}</div>
              </div>
              <div v-if="entrenoSel.steps?.length">
                <div class="small fw-semibold text-muted mb-2">Intervalos</div>
                <ol class="mb-0 ps-3">
                  <li v-for="(s, i) in entrenoSel.steps" :key="i" class="mb-2">
                    <strong>{{ entrenoPasoLabel(s) }}</strong>
                    <span v-if="s.duration_seconds" class="text-muted"> · {{ fmtDuration(s.duration_seconds) }}</span>
                    <span v-if="s.sets && s.sets > 1" class="text-muted"> · {{ s.sets }} series</span>
                    <span v-if="fmtStepIntensity(s)" class="text-muted"> · {{ fmtStepIntensity(s) }}</span>
                  </li>
                </ol>
              </div>
            </template>
            <pre v-else class="small bg-light p-3 rounded coros-json">{{ detalleJson }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { Line } from 'vue-chartjs';
import CorosEstadoEntrenamiento from '../components/CorosEstadoEntrenamiento.vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LineController,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement, LineController, Filler);

export default {
  name: 'SaludCoros',
  components: { Line, CorosEstadoEntrenamiento },
  data() {
    return {
      status: null,
      dashboard: null,
      actividades: [],
      workouts: [],
      schedule: null,
      scheduleItems: [],
      proximoEntreno: null,
      tabActivo: 'metricas',
      tabs: [
        { id: 'metricas', label: 'Métricas' },
        { id: 'sueno', label: 'Sueño' },
        { id: 'actividades', label: 'Actividades' },
        { id: 'entrenos', label: 'Entrenos' },
      ],
      error: null,
      mensaje: null,
      sincronizando: false,
      syncDays: 0,
      cargando: false,
      cargandoRemoto: false,
      cargandoDetalle: false,
      detalle: null,
      actividadSel: null,
      entrenoSel: null,
      modalModo: '',
      trendsDays: 28,
    };
  },
  computed: {
    chartFitness() {
      const t = this.dashboard?.trends;
      if (!t?.labels?.length) return { labels: [], datasets: [] };
      const hasVo2 = t.vo2max?.some((v) => v != null);
      const hasLoad = t.training_load?.some((v) => v != null);
      const hasStamina = t.stamina_level?.some((v) => v != null);
      const hasAti = t.ati?.some((v) => v != null);
      const hasCti = t.cti?.some((v) => v != null);
      if (!hasVo2 && !hasLoad && !hasStamina && !hasAti && !hasCti) return { labels: [], datasets: [] };
      const datasets = [];
      if (hasVo2) {
        datasets.push({
          label: 'VO2max',
          data: t.vo2max,
          borderColor: '#198754',
          tension: 0.2,
          pointRadius: 2,
          yAxisID: 'y',
        });
      }
      if (hasStamina) {
        datasets.push({
          label: 'Estado de carrera',
          data: t.stamina_level,
          borderColor: '#6f42c1',
          tension: 0.2,
          pointRadius: 2,
          yAxisID: 'y1',
        });
      }
      if (hasAti) {
        datasets.push({
          label: 'Carga (ATI)',
          data: t.ati,
          borderColor: '#fd7e14',
          tension: 0.2,
          pointRadius: 2,
          yAxisID: 'y1',
        });
      } else if (hasLoad) {
        datasets.push({
          label: 'Carga diaria',
          data: t.training_load,
          borderColor: '#fd7e14',
          tension: 0.2,
          pointRadius: 2,
          yAxisID: 'y1',
        });
      }
      if (hasCti) {
        datasets.push({
          label: 'Estado de forma (CTI)',
          data: t.cti,
          borderColor: '#0dcaf0',
          tension: 0.2,
          pointRadius: 2,
          yAxisID: 'y1',
        });
      }
      return { labels: t.labels, datasets };
    },
    chartOptsFitness() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: {
          x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
          y: { type: 'linear', position: 'left', title: { display: true, text: 'VO2max' } },
          y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Carga / Forma' } },
        },
      };
    },
    chartHrv() {
      const t = this.dashboard?.trends;
      if (!t?.labels?.length) return { labels: [], datasets: [] };
      return {
        labels: t.labels,
        datasets: [
          {
            label: 'HRV (ms)',
            data: t.hrv,
            borderColor: '#0d6efd',
            backgroundColor: 'rgba(13,110,253,0.08)',
            tension: 0.2,
            pointRadius: 2,
            fill: true,
          },
          {
            label: 'FC reposo',
            data: t.rhr,
            borderColor: '#dc3545',
            tension: 0.2,
            pointRadius: 2,
          },
        ],
      };
    },
    chartSleep() {
      const s = this.dashboard?.sleepTrends;
      if (!s?.labels?.length) return { labels: [], datasets: [], stacked: false };
      const hasStages = (s.deep || []).some((v) => v != null)
        || (s.rem || []).some((v) => v != null)
        || (s.light || []).some((v) => v != null);
      if (hasStages) {
        return {
          stacked: true,
          labels: s.labels,
          datasets: [
            { label: 'Profundo', data: s.deep, borderColor: '#1e3a8a', backgroundColor: 'rgba(30,58,138,0.5)', fill: true, stack: 'sleep' },
            { label: 'REM', data: s.rem, borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.5)', fill: true, stack: 'sleep' },
            { label: 'Ligero', data: s.light, borderColor: '#60a5fa', backgroundColor: 'rgba(96,165,250,0.4)', fill: true, stack: 'sleep' },
          ],
        };
      }
      return {
        stacked: false,
        labels: s.labels,
        datasets: [
          {
            label: 'Sueño total (min)',
            data: s.total,
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30,58,138,0.12)',
            tension: 0.2,
            pointRadius: 2,
            fill: true,
          },
        ],
      };
    },
    sleepNoches() {
      const s = this.dashboard?.sleepTrends;
      if (!s?.labels?.length) return [];
      return s.labels.map((dia, i) => ({
        dia,
        total: s.total?.[i],
        deep: s.deep?.[i],
        rem: s.rem?.[i],
        light: s.light?.[i],
        score: s.score?.[i],
      })).reverse();
    },
    sleepNochesSlice() {
      return this.sleepNoches.slice(0, 14);
    },
    chartSleepData() {
      return { labels: this.chartSleep.labels, datasets: this.chartSleep.datasets };
    },
    chartOpts() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: { x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } } },
      };
    },
    chartOptsStacked() {
      return {
        ...this.chartOpts,
        scales: {
          x: { stacked: true, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
          y: { stacked: true, title: { display: true, text: 'minutos' } },
        },
      };
    },
    scheduleEntities() {
      return this.schedule?.entities || [];
    },
    modalTitulo() {
      if (this.modalModo === 'entreno') return this.entrenoSel?.name || 'Detalle entreno';
      return this.actividadSel?.name || 'Detalle actividad';
    },
    detalleJson() {
      return this.detalle ? JSON.stringify(this.detalle, null, 2) : '';
    },
  },
  watch: {
    tabActivo(val) {
      if (val === 'entrenos' && this.status?.configured) {
        this.cargarEntrenos();
      }
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.cargando = true;
      this.error = null;
      try {
        const { data: st } = await axios.get('/api/coros/status');
        this.status = st;
        if (st.configured) {
          await Promise.all([this.cargarDashboard(), this.cargarActividades(), this.cargarProximoEntreno()]);
          this.syncEnBackground();
        }
      } catch (e) {
        this.error = e.response?.data?.error || 'No se pudo cargar Coros.';
      } finally {
        this.cargando = false;
      }
    },
    async cargarDashboard() {
      const { data } = await axios.get('/api/coros/dashboard', { params: { days: this.trendsDays } });
      this.dashboard = data;
    },
    async cargarActividades() {
      const { data } = await axios.get('/api/coros/activities', { params: { limit: 100 } });
      this.actividades = data.activities || [];
    },
    async sincronizar(days) {
      this.sincronizando = true;
      this.syncDays = days;
      this.error = null;
      this.mensaje = null;
      try {
        const { data } = await axios.post('/api/coros/sync', { days });
        this.mensaje = data.message;
        if (data.errors?.length) {
          this.mensaje += ` (avisos: ${data.errors.join('; ')})`;
        }
        const { data: st } = await axios.get('/api/coros/status');
        this.status = st;
        await Promise.all([this.cargarDashboard(), this.cargarActividades(), this.cargarProximoEntreno()]);
        if (this.tabActivo === 'entrenos') {
          await this.cargarEntrenos(true);
        }
      } catch (e) {
        this.error = e.response?.data?.error || 'Error al sincronizar.';
      } finally {
        this.sincronizando = false;
        this.syncDays = 0;
      }
    },
    async cargarProximoEntreno() {
      try {
        const { data } = await axios.get('/api/coros/next-workout');
        this.proximoEntreno = data.workout || null;
      } catch {
        this.proximoEntreno = null;
      }
    },
    async syncEnBackground() {
      try {
        await axios.post('/api/coros/sync/auto');
        await Promise.all([this.cargarDashboard(), this.cargarActividades(), this.cargarProximoEntreno()]);
        if (this.tabActivo === 'entrenos') {
          await this.cargarEntrenos();
        }
        await this.cargarProximoEntreno();
        const { data: st } = await axios.get('/api/coros/status');
        this.status = st;
      } catch {
        /* sync automática opcional */
      }
    },
    async cargarEntrenos(force = false) {
      await Promise.all([this.cargarWorkouts(force), this.cargarSchedule(force)]);
    },
    async cargarWorkouts(force = false) {
      this.cargandoRemoto = true;
      try {
        const params = force ? { refresh: '1' } : {};
        const { data } = await axios.get('/api/coros/workouts', { params });
        this.workouts = data.workouts || [];
      } catch (e) {
        this.error = e.response?.data?.error || 'Error al cargar entrenos.';
      } finally {
        this.cargandoRemoto = false;
      }
    },
    async cargarSchedule(force = false) {
      this.cargandoRemoto = true;
      try {
        const params = force ? { refresh: '1' } : {};
        const { data } = await axios.get('/api/coros/schedule', { params });
        this.scheduleItems = data.items || [];
        if (data.items?.length) {
          this.proximoEntreno = data.items[0];
        }
      } catch (e) {
        this.error = e.response?.data?.error || 'Error al cargar calendario.';
      } finally {
        this.cargandoRemoto = false;
      }
    },
    verEntreno(item) {
      this.modalModo = 'entreno';
      this.entrenoSel = item;
      this.actividadSel = null;
      this.detalle = { steps: item.steps || [] };
      this.cargandoDetalle = false;
    },
    async verDetalle(a) {
      this.modalModo = 'actividad';
      this.actividadSel = a;
      this.entrenoSel = null;
      this.detalle = {};
      this.cargandoDetalle = true;
      try {
        const { data } = await axios.get(`/api/coros/activities/${a.activity_id}`, {
          params: { sportType: a.sport_type || 0 },
        });
        this.detalle = data;
      } catch (e) {
        this.detalle = { error: e.response?.data?.error || e.message };
      } finally {
        this.cargandoDetalle = false;
      }
    },
    cerrarDetalle() {
      this.detalle = null;
      this.actividadSel = null;
      this.entrenoSel = null;
      this.modalModo = '';
    },
    fmtNum(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—';
      return Math.round(Number(v));
    },
    fmtDec(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—';
      return Number(v).toFixed(1);
    },
    esHoy(item) {
      if (!item) return false;
      const s = String(item.happen_day || '');
      if (s.length !== 8) return false;
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      return s === `${y}${m}${d}`;
    },
    fmtSleep(min) {
      if (min == null) return '—';
      const h = Math.floor(min / 60);
      const m = Math.round(min % 60);
      return `${h}h ${m}m`;
    },
    fmtSleepShort(min) {
      if (min == null) return '—';
      return `${Math.round(min)} m`;
    },
    fmtDiaCorto(iso) {
      const s = String(iso || '');
      if (s.length !== 10) return s || '—';
      return `${s.slice(8, 10)}/${s.slice(5, 7)}`;
    },
    fmtDuration(sec) {
      if (sec == null) return '—';
      const s = Number(sec);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h) return `${h}h ${m}m`;
      return `${m} min`;
    },
    fmtDistance(m) {
      if (m == null) return '—';
      const km = Number(m) / 1000;
      return km >= 1 ? `${km.toFixed(1)} km` : `${Math.round(m)} m`;
    },
    fmtEntrenoDia(item) {
      if (!item) return '—';
      if (item.day_label) return item.day_label;
      const s = String(item.happen_day || '');
      if (s.length === 8) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
      return s || '—';
    },
    entrenoPasoLabel(step) {
      if (!step) return 'Paso';
      return step.line?.replace(/^\+\s*/, '') || step.name || 'Paso';
    },
    entrenoDescripcion(item) {
      if (!item) return '';
      if (item.description && String(item.description).trim()) return String(item.description).trim();
      return '';
    },
    fmtStepIntensity(step) {
      if (!step) return '';
      if (step.intensity_label) return step.intensity_label;
      const lo = step.intensity_low;
      const hi = step.intensity_high;
      if (lo == null && hi == null) return '';
      if (step.intensity_type === 2 || (lo >= 40 && lo <= 230)) return `${lo}–${hi} bpm`;
      if (step.intensity_type === 6) return `${lo}–${hi} W`;
      return `${lo}–${hi}`;
    },
    entrenoDescripcionPreview(item, maxLines = 3) {
      let text = this.entrenoDescripcion(item);
      if (!text && item?.steps?.length) {
        text = item.steps
          .map((s) => s.line || (s.name ? `+ ${s.name}` : null))
          .filter(Boolean)
          .join('\n');
      }
      if (!text) return '';
      const lines = text.split('\n');
      if (lines.length <= maxLines) return text;
      return `${lines.slice(0, maxLines).join('\n')}\n…`;
    },
    fmtActivityDate(a) {
      if (!a) return '—';
      if (typeof a === 'object') {
        if (a.date_label) return a.date_label;
        if (a.dia) {
          const [y, m, d] = String(a.dia).split('-');
          if (y && m && d) return `${d}/${m}/${y}`;
        }
        if (a.start_time != null) return this.fmtActivityDate(a.start_time);
        return '—';
      }
      const s = String(a).trim();
      if (/^\d{8}$/.test(s)) {
        return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
      }
      const n = Number(s);
      if (Number.isFinite(n) && n > 0) {
        const ms = n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          return `${dd}/${mm}/${d.getFullYear()}`;
        }
      }
      return s;
    },
  },
};
</script>

<style scoped>
.coros-next-workout {
  background: linear-gradient(135deg, #0d6efd 0%, #084298 100%);
  color: #fff;
}
.coros-next-icon {
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}
.coros-next-label {
  letter-spacing: 0.06em;
  opacity: 0.85;
}
.coros-chart-wrap {
  height: 320px;
  position: relative;
}
.coros-json {
  max-height: 60vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
.coros-desc-preview,
.coros-desc-full {
  white-space: pre-line;
  line-height: 1.45;
  word-break: break-word;
}
</style>
